import React, { useEffect, useMemo, useState } from 'react';
import {
  Sparkles,
  Plus,
  Minus,
  Save,
  AlertTriangle,
  Coins,
  TrendingUp,
  History,
  FileText,
} from 'lucide-react';
import TimeRangePicker from './TimeRangePicker';
import ConfirmModal from './ConfirmModal';
import GrantCreditsModal from './GrantCreditsModal';
import DeductCreditsModal from './DeductCreditsModal';
import TransactionHistoryTable from './TransactionHistoryTable';
import {
  getTenantCreditsSummary,
  getTenantPricing,
  updateTenantPricing,
  getPricing,
  type TenantCreditsSummary,
  type TenantPricingGetResponse,
  type TenantPricingPutBody,
  type AgentPricingConfig,
  type TimeRangeValue,
  resolveTimeRange,
} from '../../services/aiUsageApi';
import { RubyLoader } from '../RubyLoader';
import { RubyGemMark } from '../RubyGemMark';

interface TenantPosAiAgentsTabProps {
  tenantId: string;
  tenantName: string;
  /** Active once the tab is clicked — enables lazy loading. */
  active: boolean;
}

type ApiKey = 'anthropic' | 'google_vertex' | 'google_cse';

interface OverrideLocalState {
  [api: string]: {
    mode: 'global' | 'override' | 'free';
    value: string;
  };
}

/**
 * TenantPosAiAgentsTab — per-tenant POS AI admin surface (spec §8.5).
 *
 * Rendered as a tab inside TenantProfile, only when the tenant has
 * pkg_pos_agents in their active packages. Tab content is lazy-loaded on
 * first click (`active` prop gates all network calls).
 */
const TenantPosAiAgentsTab: React.FC<TenantPosAiAgentsTabProps> = ({
  tenantId,
  tenantName,
  active,
}) => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ preset: '30d' });
  const [summary, setSummary] = useState<TenantCreditsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [pricing, setPricing] = useState<TenantPricingGetResponse | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);

  const [globalPricing, setGlobalPricing] = useState<AgentPricingConfig | null>(null);

  const [overrideDraft, setOverrideDraft] = useState<OverrideLocalState>({});
  const [minPurchaseOverride, setMinPurchaseOverride] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlag, setSavedFlag] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const [showGrant, setShowGrant] = useState(false);
  const [showDeduct, setShowDeduct] = useState(false);
  const [balance, setBalance] = useState<number>(0);

  const [showHistory, setShowHistory] = useState(false);

  // Lazy load on first click
  useEffect(() => {
    if (!active || hasLoaded) return;
    setHasLoaded(true);
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Refetch summary when time range changes
  useEffect(() => {
    if (!hasLoaded) return;
    void loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange.preset, timeRange.fromIso, timeRange.toIso]);

  const loadAll = async () => {
    await Promise.allSettled([loadSummary(), loadPricing(), loadGlobalPricing()]);
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const r = resolveTimeRange(timeRange);
      const s = await getTenantCreditsSummary(tenantId, {
        preset: timeRange.preset,
        fromIso: r.fromIso,
        toIso: r.toIso,
      });
      setSummary(s);
      setBalance(s.balance);
    } catch (err: any) {
      setSummaryError(err?.message || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadPricing = async () => {
    setPricingLoading(true);
    setPricingError(null);
    try {
      const p = await getTenantPricing(tenantId);
      setPricing(p);
      // Hydrate draft from existing override
      const draft: OverrideLocalState = {};
      (['anthropic', 'google_vertex', 'google_cse'] as ApiKey[]).forEach((api) => {
        const o = p.override?.apis?.[api];
        if (o?.free) {
          draft[api] = { mode: 'free', value: '' };
        } else if (o?.burnRateOverride !== undefined && o.burnRateOverride !== null) {
          draft[api] = { mode: 'override', value: String(o.burnRateOverride) };
        } else {
          draft[api] = { mode: 'global', value: '' };
        }
      });
      setOverrideDraft(draft);
      setMinPurchaseOverride(
        p.override?.minPurchaseCredits ? String(p.override.minPurchaseCredits) : '',
      );
      setNote(p.override?.note || '');
    } catch (err: any) {
      setPricingError(err?.message || 'Failed to load tenant pricing');
    } finally {
      setPricingLoading(false);
    }
  };

  const loadGlobalPricing = async () => {
    try {
      const g = await getPricing();
      setGlobalPricing(g);
    } catch {
      // Non-fatal; global burn rates used for reference text only
    }
  };

  const handleSaveClick = () => {
    setSaveError(null);
    setShowSaveConfirm(true);
  };

  const buildPutBody = (): TenantPricingPutBody => {
    const apis: TenantPricingPutBody['apis'] = {};
    (['anthropic', 'google_vertex', 'google_cse'] as ApiKey[]).forEach((api) => {
      const d = overrideDraft[api];
      if (!d) return;
      if (d.mode === 'global') {
        // no-op → clear
        apis[api] = {};
      } else if (d.mode === 'free') {
        apis[api] = { free: true };
      } else {
        const n = parseFloat(d.value);
        if (Number.isFinite(n) && n >= 0 && n <= 50) {
          apis[api] = { burnRateOverride: n };
        }
      }
    });

    const minN = parseInt(minPurchaseOverride, 10);
    const minOverride =
      minPurchaseOverride.trim() === '' ? null : Number.isFinite(minN) && minN > 0 ? minN : null;

    return {
      apis,
      minPurchaseCredits: minOverride,
      note: note.trim() || undefined,
    };
  };

  const handleConfirmSave = async () => {
    setSaveBusy(true);
    setSaveError(null);
    try {
      const body = buildPutBody();
      const updated = await updateTenantPricing(tenantId, body);
      setPricing(updated);
      setSavedFlag(true);
      setShowSaveConfirm(false);
      setTimeout(() => setSavedFlag(false), 3000);
    } catch (err: any) {
      setSaveError(err?.message || 'Save failed');
      setShowSaveConfirm(false);
    } finally {
      setSaveBusy(false);
    }
  };

  const handleConfirmReset = async () => {
    setSaveBusy(true);
    setSaveError(null);
    try {
      await updateTenantPricing(tenantId, { resetToGlobal: true });
      await loadPricing();
      setShowResetConfirm(false);
    } catch (err: any) {
      setSaveError(err?.message || 'Reset failed');
      setShowResetConfirm(false);
    } finally {
      setSaveBusy(false);
    }
  };

  const apiLabel = (api: ApiKey) => {
    const map: Record<ApiKey, string> = {
      anthropic: 'Anthropic Claude',
      google_vertex: 'Google Vertex AI',
      google_cse: 'AI Grounding',
    };
    return map[api];
  };

  const globalBurn = (api: ApiKey): number => globalPricing?.apis[api]?.burnRate ?? 5.0;

  const totalConsumptionRow = useMemo(() => {
    if (!summary) return null;
    const total = summary.perApi.reduce(
      (acc, r) => ({
        calls: acc.calls + r.calls,
        realCost: acc.realCost + r.realCostUsd,
        credits: acc.credits + r.creditsConsumed,
        revenue: acc.revenue + r.revenueUsd,
      }),
      { calls: 0, realCost: 0, credits: 0, revenue: 0 },
    );
    return total;
  }, [summary]);

  if (!active) return null;

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-rose-600" />
          <div>
            <h2 className="text-sm font-semibold text-stone-900">POS AI Agents</h2>
            <p className="text-[11px] text-stone-500">{tenantName}</p>
          </div>
        </div>
        <TimeRangePicker value={timeRange} onChange={setTimeRange} compact />
      </div>

      {/* Balance + Quick Actions */}
      <section className="bg-white border border-stone-200 rounded-xl shadow-sm p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-rose-100 rounded-xl flex items-center justify-center">
            <Coins size={22} className="text-rose-600" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-stone-500">Current balance</div>
            <div className="text-2xl font-bold text-stone-900">{balance.toLocaleString()} credits</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowGrant(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 min-h-[44px]"
          >
            <Plus size={14} />
            Grant credits
          </button>
          <button
            type="button"
            onClick={() => setShowDeduct(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm bg-rose-600 text-white hover:bg-rose-700 min-h-[44px]"
          >
            <Minus size={14} />
            Deduct credits
          </button>
        </div>
      </section>

      {/* Loaded vs Spent — scoped to the selected calendar range above. Balance
          card (all-time) stays separate; these move with the date range. */}
      {summary && (
        <section>
          <p className="text-[11px] text-stone-500 mb-2">
            Loaded &amp; spent for the selected period
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <LoadSpendCard
              label="Total Loaded"
              value={
                summary.totals.loadedCredits ??
                (summary.totals.grantedCredits ?? 0) + (summary.totals.purchasedCredits ?? 0)
              }
              sub={`${(summary.totals.grantedCredits ?? 0).toLocaleString()} granted · ${(summary.totals.purchasedCredits ?? 0).toLocaleString()} purchased`}
              tone="emerald"
            />
            <LoadSpendCard
              label="Total Spent"
              value={summary.totals.consumedCredits ?? summary.totals.credits ?? 0}
              sub={`${(summary.totals.calls ?? 0).toLocaleString()} calls`}
              tone="purple"
            />
            <LoadSpendCard
              label="Adjustments"
              value={(summary.totals.refundedCredits ?? 0) - (summary.totals.deductedCredits ?? 0)}
              sub={`${(summary.totals.refundedCredits ?? 0).toLocaleString()} refunded · ${(summary.totals.deductedCredits ?? 0).toLocaleString()} deducted`}
              tone="blue"
            />
          </div>
        </section>
      )}

      {/* Consumption summary */}
      <section className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-stone-900">Consumption Summary</h3>
          {summaryLoading && <RubyGemMark size={12} drawing />}
        </div>
        {summaryError ? (
          <div className="p-4 text-xs text-rose-700 bg-rose-50 border-y border-rose-200 flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            {summaryError}
          </div>
        ) : !summary ? (
          summaryLoading ? (
            <RubyLoader variant="section" label="Loading…" className="py-6" />
          ) : (
            <div className="p-6 text-center text-xs text-stone-500">No data</div>
          )
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                      API
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                      Calls
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                      Real cost
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                      Credits
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {summary.perApi.map((r) => (
                    <tr key={r.api}>
                      <td className="px-3 py-2 text-sm text-stone-800">{apiDisplayName(r.api)}</td>
                      <td className="px-3 py-2 text-sm text-stone-800 text-right">
                        {r.calls.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-800 text-right">
                        ${r.realCostUsd.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-800 text-right">
                        {r.creditsConsumed.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-900 text-right font-semibold">
                        ${r.revenueUsd.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  {totalConsumptionRow && (
                    <tr className="bg-stone-50 font-semibold">
                      <td className="px-3 py-2 text-sm text-stone-900">Total</td>
                      <td className="px-3 py-2 text-sm text-stone-900 text-right">
                        {totalConsumptionRow.calls.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-900 text-right">
                        ${totalConsumptionRow.realCost.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-900 text-right">
                        {totalConsumptionRow.credits.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-900 text-right">
                        ${totalConsumptionRow.revenue.toFixed(2)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-600">
              <span className="flex items-center gap-1.5">
                <TrendingUp size={12} className="text-emerald-600" />
                Gross margin:{' '}
                <span className="font-semibold text-emerald-700">
                  ${(summary.totals.grossMarginUsd ?? 0).toFixed(2)}
                </span>{' '}
                ({(summary.totals.grossMarginPercent ?? 0).toFixed(0)}%)
              </span>
            </div>
          </>
        )}

        {/* Operation Breakdown */}
        {summary && summary.perOperation.length > 0 && (
          <div className="border-t border-stone-100 px-4 py-3">
            <h4 className="text-[11px] uppercase tracking-wide text-stone-500 font-medium mb-2">
              Operation Breakdown
            </h4>
            <table className="w-full">
              <tbody className="divide-y divide-stone-100">
                {summary.perOperation.map((o) => (
                  <tr key={o.operation}>
                    <td className="py-1.5 text-xs font-mono text-stone-600">{o.operation}</td>
                    <td className="py-1.5 text-xs text-stone-800 text-right">
                      {o.calls.toLocaleString()} calls
                    </td>
                    <td className="py-1.5 text-xs text-stone-800 text-right font-semibold">
                      {o.credits.toLocaleString()} cr
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Per-API Pricing Overrides */}
      <section className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-stone-900">Per-API Pricing Overrides</h3>
          {pricingLoading && <RubyGemMark size={12} drawing />}
        </div>
        {pricingError ? (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            {pricingError}
          </div>
        ) : (
          <div className="space-y-3">
            {(['anthropic', 'google_vertex', 'google_cse'] as ApiKey[]).map((api) => {
              const d = overrideDraft[api] || { mode: 'global' as const, value: '' };
              const gBurn = globalBurn(api);
              return (
                <div key={api} className="border border-stone-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-stone-900 mb-2">{apiLabel(api)}</div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer min-h-[36px]">
                      <input
                        type="radio"
                        checked={d.mode === 'global'}
                        onChange={() =>
                          setOverrideDraft((prev) => ({
                            ...prev,
                            [api]: { mode: 'global', value: '' },
                          }))
                        }
                        className="text-emerald-600"
                      />
                      Global default ({gBurn.toFixed(1)}×)
                    </label>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer min-h-[36px]">
                      <input
                        type="radio"
                        checked={d.mode === 'override'}
                        onChange={() =>
                          setOverrideDraft((prev) => ({
                            ...prev,
                            [api]: { mode: 'override', value: prev[api]?.value || String(gBurn) },
                          }))
                        }
                        className="text-amber-600"
                      />
                      Override:
                      <input
                        type="number"
                        min={0}
                        max={50}
                        step={0.1}
                        value={d.value}
                        disabled={d.mode !== 'override'}
                        onChange={(e) =>
                          setOverrideDraft((prev) => ({
                            ...prev,
                            [api]: { mode: 'override', value: e.target.value },
                          }))
                        }
                        placeholder="5.0"
                        className="w-16 px-2 py-1 bg-stone-50 border border-stone-200 rounded-md text-stone-800 text-xs disabled:opacity-40 min-h-[32px]"
                      />
                      ×
                    </label>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer min-h-[36px]">
                      <input
                        type="radio"
                        checked={d.mode === 'free'}
                        onChange={() =>
                          setOverrideDraft((prev) => ({
                            ...prev,
                            [api]: { mode: 'free', value: '' },
                          }))
                        }
                        className="text-rose-600"
                      />
                      Free
                    </label>
                  </div>
                </div>
              );
            })}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <label className="block">
                <span className="text-[11px] text-stone-500">
                  Minimum purchase override (blank = global)
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={minPurchaseOverride}
                  onChange={(e) => setMinPurchaseOverride(e.target.value)}
                  placeholder={`${pricing?.resolved.minPurchaseCredits ?? '—'}`}
                  className="mt-1 w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm min-h-[44px]"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-stone-500">Note (shown in audit log)</span>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Beta customer, Q2 2026"
                  className="mt-1 w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm min-h-[44px]"
                />
              </label>
            </div>
          </div>
        )}

        {saveError && (
          <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {saveError}
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100 gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowResetConfirm(true)}
            disabled={saveBusy}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50 min-h-[44px]"
          >
            Reset all to global
          </button>
          <div className="flex items-center gap-2">
            {savedFlag && (
              <span className="text-xs font-semibold text-emerald-700">Saved</span>
            )}
            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saveBusy}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 min-h-[44px]"
            >
              <Save size={14} />
              Save &amp; Publish
            </button>
          </div>
        </div>
      </section>

      {/* Audit log */}
      {pricing?.override?.auditLog && pricing.override.auditLog.length > 0 && (
        <section className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <History size={14} className="text-stone-500" />
            <h3 className="text-sm font-semibold text-stone-900">Recent Pricing Changes</h3>
          </div>
          <ul className="divide-y divide-stone-100">
            {pricing.override.auditLog.slice(0, 20).map((a, i) => (
              <li key={i} className="py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-stone-400 font-mono text-[11px]">
                    {new Date(a.at).toLocaleString()}
                  </span>
                  <span className="text-stone-500">{a.by}</span>
                </div>
                <div className="text-stone-700 mt-0.5">
                  <code className="text-[11px]">{JSON.stringify(a.diff)}</code>
                </div>
                {a.note && <div className="text-stone-400 text-[11px] mt-0.5">{a.note}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Transaction History */}
      <div>
        {showHistory ? (
          <TransactionHistoryTable
            tenantId={tenantId}
            context="admin"
            tenantDisplayName={tenantName}
            initialTimeRange={timeRange}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowHistory(true)}
            className="w-full bg-white border border-dashed border-stone-300 rounded-xl px-4 py-4 text-sm font-semibold text-stone-700 hover:bg-stone-50 hover:border-stone-400 transition-colors inline-flex items-center justify-center gap-2 min-h-[56px]"
          >
            <FileText size={14} />
            View Transaction History
          </button>
        )}
      </div>

      {/* Modals */}
      <GrantCreditsModal
        open={showGrant}
        tenantId={tenantId}
        tenantName={tenantName}
        currentBalance={balance}
        onClose={() => setShowGrant(false)}
        onGranted={(b) => {
          setBalance(b);
          void loadSummary();
        }}
      />
      <DeductCreditsModal
        open={showDeduct}
        tenantId={tenantId}
        tenantName={tenantName}
        currentBalance={balance}
        onClose={() => setShowDeduct(false)}
        onDeducted={(b) => {
          setBalance(b);
          void loadSummary();
        }}
      />
      <ConfirmModal
        open={showSaveConfirm}
        title="Save tenant pricing overrides?"
        message={
          <>
            This will update billing immediately for <strong>{tenantName}</strong>. Future AI
            operations use the new rates. Existing ledger entries stay at their historical rates.
          </>
        }
        confirmLabel="Save & Publish"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveConfirm(false)}
        tone="emerald"
        busy={saveBusy}
      />
      <ConfirmModal
        open={showResetConfirm}
        title="Reset tenant pricing to global?"
        message={
          <>
            This removes all tenant-specific overrides for <strong>{tenantName}</strong>. The tenant
            will revert to global burn rates and minimum purchase. This is recorded in the audit
            log.
          </>
        }
        confirmLabel="Reset to global"
        cancelLabel="Cancel"
        onConfirm={handleConfirmReset}
        onCancel={() => setShowResetConfirm(false)}
        tone="amber"
        busy={saveBusy}
      />
    </div>
  );
};

// Friendly provider/meter names for the per-API consumption table.
const API_DISPLAY: Record<string, string> = {
  anthropic: 'Anthropic Claude',
  google_vertex: 'Google Vertex AI',
  google_cse: 'AI Grounding',
  mcp_connector: 'Connector tooling',
};
const apiDisplayName = (api: string): string => API_DISPLAY[api] || api;

const LoadSpendCard: React.FC<{
  label: string;
  value: number;
  sub: string;
  tone: 'emerald' | 'purple' | 'blue';
}> = ({ label, value, sub, tone }) => {
  const palette: Record<string, string> = {
    emerald: 'text-emerald-700',
    purple: 'text-rose-700',
    blue: 'text-blue-700',
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-stone-500 font-medium">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${palette[tone]}`}>
        {value.toLocaleString()} <span className="text-xs font-medium text-stone-400">cr</span>
      </div>
      <div className="text-[11px] text-stone-400 mt-0.5">{sub}</div>
    </div>
  );
};

export default TenantPosAiAgentsTab;
